/**
 * EscalationTimeline - Visual preview of escalation schedule
 * Shows the reminder flow based on current config values
 */

import React from 'react';
import { Bell, AlertTriangle, AlertCircle, ShieldAlert } from 'lucide-react';

interface EscalationTimelineProps {
  startDay: number;
  warnDay: number;
  urgentDay: number;
  escalationHours: number;
  maxTier: number;
}

export const EscalationTimeline: React.FC<EscalationTimelineProps> = ({
  startDay,
  warnDay,
  urgentDay,
  escalationHours,
  maxTier,
}) => {
  const steps = [
    {
      day: startDay,
      label: 'Tier 1 - Reminder',
      description: 'In-app notification to assigned staff',
      icon: <Bell className="w-4 h-4" />,
      color: 'bg-blue-500',
      textColor: 'text-blue-700 dark:text-blue-300',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    },
    {
      day: warnDay,
      label: 'Tier 2 - Overdue',
      description: 'Email + in-app notification',
      icon: <AlertTriangle className="w-4 h-4" />,
      color: 'bg-orange-500',
      textColor: 'text-orange-700 dark:text-orange-300',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    },
    {
      day: urgentDay,
      label: 'Tier 3 - Urgent',
      description: 'SMS + email + in-app, all channels',
      icon: <AlertCircle className="w-4 h-4" />,
      color: 'bg-red-500',
      textColor: 'text-red-700 dark:text-red-300',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
    },
  ];

  if (maxTier >= 4) {
    steps.push({
      day: urgentDay,
      label: `Tier 4 - Supervisor (+${escalationHours}h)`,
      description: 'Supervisor notified if unacknowledged',
      icon: <ShieldAlert className="w-4 h-4" />,
      color: 'bg-purple-500',
      textColor: 'text-purple-700 dark:text-purple-300',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    });
  }

  return (
    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        Monthly Contact Escalation Timeline
      </h4>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-gray-200 dark:bg-gray-600" />

        <div className="space-y-4">
          {steps.map((step, i) => (
            <div key={i} className="flex items-start gap-3 relative">
              {/* Dot */}
              <div className={`w-8 h-8 rounded-full ${step.color} flex items-center justify-center text-white flex-shrink-0 z-10`}>
                {step.icon}
              </div>
              {/* Content */}
              <div className={`flex-1 ${step.bgColor} rounded-lg px-3 py-2`}>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${step.textColor}`}>
                    Day {step.day}
                  </span>
                  <span className={`text-sm font-medium ${step.textColor}`}>
                    {step.label}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {step.description}
                </p>
              </div>
            </div>
          ))}

          {/* Month end */}
          <div className="flex items-start gap-3 relative">
            <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-white flex-shrink-0 z-10">
              <span className="text-xs font-bold">31</span>
            </div>
            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Month End - Compliance deadline
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Each tier escalates after {escalationHours}h with no acknowledgment.
        Auto-acknowledges when a progress note is submitted.
      </p>
    </div>
  );
};
